import { useMemo, useState } from "react";
import { useAndesRequests } from "@/hooks/queries";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Field, Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { EmptyState, TBody, TD, TH, THead, TR, Table } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { Badge } from "@/components/ui/Badge";

/** Local calendar date N days before today (YYYY-MM-DD). */
function daysAgoIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  // Local Y-M-D (not UTC) so "a week ago" matches the operator's day.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayLocalIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function estadoTone(estado: string): "success" | "warning" | "danger" | "neutral" {
  const e = estado.trim().toLowerCase();
  if (e.includes("aprob")) return "success";
  if (e.includes("espera")) return "warning";
  if (e.includes("rechaz")) return "danger";
  return "neutral";
}

const DEFAULT_FROM = () => daysAgoIso(7);
const DEFAULT_TO = () => todayLocalIso();

export function AndesQueue() {
  // Always default to last 7 days → today, and search on first paint.
  const [dateFromInput, setDateFromInput] = useState(DEFAULT_FROM);
  const [dateToInput, setDateToInput] = useState(DEFAULT_TO);
  const [filters, setFilters] = useState<{ dateFrom: string; dateTo: string }>(() => ({
    dateFrom: DEFAULT_FROM(),
    dateTo: DEFAULT_TO(),
  }));

  const query = useAndesRequests(filters);

  const total = query.data?.data.totalRecords ?? 0;
  const requests = query.data?.data.requests ?? [];

  const counts = useMemo(() => {
    const c = { aprobada: 0, espera: 0, rechazada: 0, otro: 0 };
    for (const r of requests) {
      const e = r.estado.toLowerCase();
      if (e.includes("aprob")) c.aprobada++;
      else if (e.includes("espera")) c.espera++;
      else if (e.includes("rechaz")) c.rechazada++;
      else c.otro++;
    }
    return c;
  }, [requests]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFilters({ dateFrom: dateFromInput, dateTo: dateToInput });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Andes request queue</h1>
          <p className="text-sm text-slate-500 mt-1">
            Solicitudes registradas en el panel Coordinador de Andes SCD por rango de fecha.
            Una solicitud queda en <code className="text-xs">En espera</code> hasta que el equipo
            de Andes la apruebe o rechace.
          </p>
        </div>
      </div>

      <Card className="p-4">
        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
          <Field label="Desde" className="w-40">
            <Input
              type="date"
              value={dateFromInput}
              onChange={(e) => setDateFromInput(e.target.value)}
              required
            />
          </Field>
          <Field label="Hasta" className="w-40">
            <Input
              type="date"
              value={dateToInput}
              onChange={(e) => setDateToInput(e.target.value)}
              required
            />
          </Field>
          <Button type="submit" loading={query.isFetching}>
            Buscar
          </Button>
          {!query.isPending && !query.isError && (
            <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
              <span>Total: <span className="font-semibold text-slate-700">{total}</span></span>
              <span className="text-green-700">{counts.aprobada} aprobadas</span>
              <span className="text-amber-700">{counts.espera} en espera</span>
              <span className="text-red-700">{counts.rechazada} rechazadas</span>
              {counts.otro > 0 && <span className="text-slate-500">{counts.otro} otros</span>}
            </div>
          )}
        </form>
      </Card>

      {query.isPending ? (
        <CenteredSpinner label="Consultando Andes…" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : requests.length === 0 ? (
        <EmptyState
          title="Sin solicitudes en este rango"
          description="Ajusta el rango de fechas y vuelve a buscar."
        />
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <THead>
              <tr>
                <TH>Solicitud</TH>
                <TH>Fecha</TH>
                <TH>Tipo</TH>
                <TH>Solicitante</TH>
                <TH>Empresa</TH>
                <TH>Estado</TH>
                <TH>Coordinador</TH>
                <TH>Observaciones</TH>
              </tr>
            </THead>
            <TBody>
              {requests.map((r) => (
                <TR key={r.solicitudWeb}>
                  <TD className="font-mono text-xs text-slate-900">{r.solicitudWeb}</TD>
                  <TD className="text-xs text-slate-600 whitespace-nowrap">
                    {r.fechaSolicitud}
                  </TD>
                  <TD className="text-xs">
                    {r.tipoCertificado.replace("Facturación Electronica - ", "")}
                  </TD>
                  <TD>
                    <div className="font-medium text-slate-900">{r.solicitante}</div>
                    <div className="font-mono text-[11px] text-slate-500">
                      {r.documentoSolicitante}
                    </div>
                  </TD>
                  <TD>
                    {r.empresa ? (
                      <>
                        <div className="text-xs text-slate-700">{r.empresa}</div>
                        <div className="font-mono text-[11px] text-slate-500">
                          {r.nitEmpresa}
                        </div>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TD>
                  <TD>
                    <Badge tone={estadoTone(r.estado)}>{r.estado}</Badge>
                  </TD>
                  <TD className="text-xs text-slate-600">{r.coordinadorExterno}</TD>
                  <TD
                    className="max-w-md text-[11px] text-slate-600"
                    title={r.observaciones}
                  >
                    {r.observaciones || <span className="text-slate-400">—</span>}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
